import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import {
    marketingExperimentTierEntity,
    marketingChannelConfigurationEntity,
    marketingObjectiveEntity,
    marketingExperimentEntity,
} from 'mj_generatedentities';
import { runAction, parseActionMessageJson } from '../../shared/run-action.util';

interface SimilarMatch {
    id?: string;
    finding?: string;
    confidence?: string;
    similarity?: number;
}

interface PreCheckParsed {
    similarMatches?: SimilarMatch[];
    blockedByLearning?: boolean;
    similarityScore?: number;
    blockingFinding?: string;
    matchedLearningId?: string;
}

interface SuggestTierResp {
    suggestedTier?: string;
    rationale?: string;
}

interface SprintCapacityTier {
    tier?: string;
    name?: string;
    free?: number;
    available?: number;
    total?: number;
    capacity?: number;
}

function isSprintCapacity(v: unknown): v is { tiers?: SprintCapacityTier[] } {
    if (typeof v !== 'object' || v === null) return false;
    const r = v as Record<string, unknown>;
    return r['tiers'] === undefined || Array.isArray(r['tiers']);
}

function isSuggestTierResp(v: unknown): v is SuggestTierResp {
    if (typeof v !== 'object' || v === null) return false;
    const r = v as Record<string, unknown>;
    const optStr = (k: string) => r[k] === undefined || typeof r[k] === 'string';
    return optStr('suggestedTier') && optStr('rationale');
}

interface PreCheckResp {
    ok: boolean;
    blocked: boolean;
    level: 'ok' | 'soft' | 'hard';
    requiresAcknowledgment: boolean;
    requiresOverride: boolean;
    hardMatches: Array<{ learningId: string; score: number; confidence: string }>;
    softMatches: Array<{ learningId: string; score: number; confidence: string }>;
    validationErrors: Array<{ field?: string; code: string; message: string; severity?: string }>;
    suggestedCode?: string;
    proposalConfidence?: { label: string; rationale: string };
    overTierCap?: boolean;
}

function isPreCheckResp(v: unknown): v is PreCheckResp {
    if (typeof v !== 'object' || v === null) return false;
    const r = v as Record<string, unknown>;
    return typeof r['ok'] === 'boolean'
        && typeof r['blocked'] === 'boolean'
        && Array.isArray(r['hardMatches'])
        && Array.isArray(r['softMatches'])
        && Array.isArray(r['validationErrors']);
}

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-propose',
    template: `
<mkt-ws-page-head
    eyebrow="Experiments · Propose"
    title="Propose an experiment."
    sub="Most experiments come from the agent — this is for when you have a specific hypothesis. The agent checks against Learnings for anti-pattern matches before queueing."
    [hasActions]="true">
    <div actions>
        <button class="pr__btn" type="button" (click)="navigate('experiments')"><i class="fa-solid fa-arrow-left"></i> Back</button>
        <button class="pr__btn pr__btn--primary" type="button" [disabled]="submitting || !canSubmit" (click)="submit()">
            <i class="fa-solid fa-check"></i> {{ submitting ? 'Submitting…' : 'Submit for agent review' }}
        </button>
    </div>
</mkt-ws-page-head>

<div class="pr__layout">
    <!-- Form -->
    <mkt-ws-card class="pr__form" [hasHead]="true" [noPadding]="true">
        <div head><span class="pr__caps-ink">Hypothesis &amp; setup</span></div>
        <div class="pr__form-body">
            <div>
                <label class="pr__caps">Experiment name</label>
                <input type="text" class="pr__input" [(ngModel)]="form.name" placeholder="Short headline for this experiment" />
            </div>
            <div>
                <label class="pr__caps">Hypothesis (one line)</label>
                <input type="text" class="pr__input" [(ngModel)]="form.hypothesis" placeholder="e.g., Reddit r/nonprofits drives qualified site traffic at near-zero cost" />
            </div>
            <div>
                <label class="pr__caps">Tactic</label>
                <input type="text" class="pr__input" [(ngModel)]="form.tactic" placeholder="Short token used by the similarity gate (e.g., gated-pdf, sponsored-inmail)" />
            </div>
            <div class="pr__row">
                <div>
                    <label class="pr__caps">Tier</label>
                    <select class="pr__input" [(ngModel)]="form.tierId">
                        <option *ngFor="let t of tiers" [value]="t.ID">{{ t.Name }}</option>
                    </select>
                </div>
                <div>
                    <label class="pr__caps">Channel</label>
                    <select class="pr__input" [(ngModel)]="form.channelId">
                        <option [value]="''">— None —</option>
                        <option *ngFor="let c of channels" [value]="c.ID">{{ channelLabel(c) }}</option>
                    </select>
                </div>
            </div>
            <div class="pr__row pr__row--3">
                <div>
                    <label class="pr__caps">Budget (USD)</label>
                    <input type="number" class="pr__input" [(ngModel)]="form.budget" placeholder="100" />
                    <div class="pr__hint">Total spend for the experiment.</div>
                </div>
                <div>
                    <label class="pr__caps">Metric name</label>
                    <input type="text" class="pr__input" [(ngModel)]="form.metricName" placeholder="e.g., CTR, ConversionsPerMonth, ReplyRate" />
                    <div class="pr__hint">What you're measuring. Same scale across all 5 number fields below.</div>
                </div>
                <div>
                    <label class="pr__caps">Metric baseline</label>
                    <input type="number" step="any" class="pr__input" [(ngModel)]="form.metricBaseline" placeholder="1.2" />
                    <div class="pr__hint">Current value before the experiment runs.</div>
                </div>
            </div>
            <div class="pr__hint pr__hint--banner">
                Thresholds must satisfy <strong>Kill &lt; Iterate min ≤ Iterate max &lt; Keep</strong>.
                Use the same scale as the baseline. Example: if baseline CTR is 1.2, try Kill 1.0 / Iterate 1.3–1.99 / Keep 2.0.
                <button class="pr__btn pr__btn--ghost pr__btn--sm" type="button" (click)="autofillThresholds()" [disabled]="!form.metricBaseline">
                    <i class="fa-solid fa-wand-magic-sparkles"></i> Auto-fill from baseline
                </button>
            </div>
            <div class="pr__row pr__row--3">
                <div>
                    <label class="pr__caps">KILL threshold</label>
                    <input type="number" step="any" class="pr__input" [(ngModel)]="form.killThreshold" placeholder="1.0" />
                    <div class="pr__hint">At or below this = kill the experiment.</div>
                </div>
                <div>
                    <label class="pr__caps">Iterate range (min–max)</label>
                    <div style="display:flex; gap:4px;">
                        <input type="number" step="any" class="pr__input" [(ngModel)]="form.iterateMin" placeholder="1.3" />
                        <input type="number" step="any" class="pr__input" [(ngModel)]="form.iterateMax" placeholder="1.99" />
                    </div>
                    <div class="pr__hint">Inside this band = iterate (try a tweaked version).</div>
                </div>
                <div>
                    <label class="pr__caps">KEEP threshold</label>
                    <input type="number" step="any" class="pr__input" [(ngModel)]="form.keepThreshold" placeholder="2.0" />
                    <div class="pr__hint">At or above this = keep (or graduate to evergreen at 12+ wks).</div>
                </div>
            </div>
            <div class="pr__row pr__row--2">
                <div>
                    <label class="pr__caps">Baseline source</label>
                    <select class="pr__input" [(ngModel)]="form.baselineSource">
                        <option value="measured">Measured (real data)</option>
                        <option value="founder-estimate">Founder estimate (unvalidated)</option>
                        <option value="cold-start">Cold start (no baseline)</option>
                    </select>
                    <div class="pr__hint">Where the baseline came from. Cold-start auto-promotes to Tier 3 per framework §3.</div>
                </div>
                <div>
                    <label class="pr__caps">Max duration (weeks)</label>
                    <input type="number" min="1" max="24" class="pr__input" [(ngModel)]="form.maxDurationWeeks" placeholder="8" />
                    <div class="pr__hint">Hard cap on the experiment window. Default 8.</div>
                </div>
            </div>

            <div *ngIf="coldStartTierWarning" class="pr__hint pr__hint--banner" style="background:#fef3c7; border-color:#fde68a;">
                <i class="fa-solid fa-circle-info"></i>
                Cold-start baseline detected — framework §3 auto-promotes this experiment to <strong>Tier 3 Explore</strong> regardless of the tier you selected above.
            </div>

            <div class="pr__row pr__row--3">
                <div>
                    <label class="pr__caps">Connects to (type) <span class="pr__req">*</span></label>
                    <select class="pr__input" [(ngModel)]="form.connectsToType">
                        <option value="">— Select connection type —</option>
                        <option value="Foundation">Foundation section</option>
                        <option value="Plan">Plan section</option>
                        <option value="Learning">Prior Learning</option>
                    </select>
                    <div class="pr__hint">What this experiment tests. Objectives are NOT valid here — they're what we measure success against, not what an experiment tests.</div>
                </div>
                <div style="grid-column: span 2;">
                    <label class="pr__caps">Connects to (reference)</label>
                    <input type="text" class="pr__input" [(ngModel)]="form.connectsToId" placeholder="ID of Foundation/Plan section or prior Learning" />
                    <div class="pr__hint">Free-text ID for now. Picker arrives when section-typed strategy ships.</div>
                </div>
            </div>

            <div>
                <label class="pr__caps">Strategic connection (rationale)</label>
                <textarea class="pr__input pr__textarea" rows="2" [(ngModel)]="form.strategicNote" placeholder="Why this matters — name the Foundation/Plan section it tests, not just the objective"></textarea>
                <div class="pr__hint">≤140 chars recommended. Generic ("grow conversions") will be rejected by the validator.</div>
            </div>

            <div>
                <label class="pr__caps">Linked objective (optional)</label>
                <select class="pr__input" [(ngModel)]="form.objectiveId">
                    <option [value]="''">— Not tied to a current objective —</option>
                    <option *ngFor="let o of objectives" [value]="o.ID">{{ o.Title }}</option>
                </select>
                <div class="pr__hint">Informational link only. Does NOT satisfy strategic connection by itself.</div>
            </div>

            <div>
                <label class="pr__caps">Update targets <span class="pr__req">*</span> (≥1 required)</label>
                <div class="pr__hint" style="margin-bottom: 8px;">What gets updated when this experiment closes. Framework §2.7 — required so the result has a clear strategic purpose.</div>
                <div *ngFor="let t of updateTargets; let i = index" class="pr__ut-row">
                    <select class="pr__input" [(ngModel)]="t.Type">
                        <option value="Foundation">Foundation</option>
                        <option value="Plan">Plan</option>
                        <option value="Learning">Learning</option>
                    </select>
                    <input type="text" class="pr__input" [(ngModel)]="t.TargetReference" placeholder="What gets updated, e.g. 'ICP confidence Weak→Moderate'" />
                    <select class="pr__input" [(ngModel)]="t.Condition">
                        <option value="Always">Always (Learning)</option>
                        <option value="Keep">If KEEP</option>
                        <option value="Iterate">If ITERATE</option>
                        <option value="Kill">If KILL</option>
                    </select>
                    <button type="button" class="pr__btn pr__btn--ghost pr__btn--sm" (click)="removeUpdateTarget(i)" title="Remove">
                        <i class="fa-solid fa-xmark"></i>
                    </button>
                </div>
                <button type="button" class="pr__btn pr__btn--ghost pr__btn--sm" (click)="addUpdateTarget()">
                    <i class="fa-solid fa-plus"></i> Add target
                </button>
            </div>

            <div>
                <label class="pr__caps">Prior-learning notes (acknowledgment / override rationale)</label>
                <textarea class="pr__input pr__textarea" rows="2" [(ngModel)]="form.priorLearningNotes" placeholder="If pre-check flags a soft or hard match, explain what's different here"></textarea>
                <div class="pr__hint">Required when pre-check returns soft match. Required + must be substantive when overriding a hard match.</div>
            </div>
        </div>
    </mkt-ws-card>

    <!-- Pre-check sidebar -->
    <div class="pr__side">
        <mkt-ws-card [hasHead]="true">
            <div head>
                <span class="pr__caps-ink">Agent pre-check</span>
                <mkt-ws-pill *ngIf="preCheck.ran && preCheck.level !== 'unknown'"
                    [variant]="preCheck.level === 'hard' ? 'bad' : preCheck.level === 'soft' ? 'warn' : 'good'">
                    {{ preCheck.level === 'hard' ? 'Hard match · Blocked'
                        : preCheck.level === 'soft' ? 'Soft match · Acknowledge'
                        : 'Pre-scan ok' }}
                </mkt-ws-pill>
            </div>
            <div class="pr__pre-body">
                <div *ngIf="!preCheck.ran && !preChecking" class="pr__pre-empty">
                    <button class="pr__btn" type="button" [disabled]="!canPreCheck" (click)="runPreCheck()">
                        <i class="fa-solid fa-shield-halved"></i> Run pre-check
                    </button>
                    <div class="pr__sub">Scans Learnings for anti-pattern matches (no save).</div>
                </div>
                <div *ngIf="preChecking" class="pr__pre-empty">
                    <i class="fa-solid fa-spinner fa-spin"></i> Scanning learnings…
                </div>
                <ng-container *ngIf="preCheck.ran && !preChecking">
                    <div *ngIf="preCheck.error" class="pr__pre-row">
                        <i class="fa-solid fa-circle-exclamation pr__warn"></i>
                        <div>
                            <div class="pr__pre-title">Pre-check couldn't run yet</div>
                            <div class="pr__sub">{{ preCheck.error }}</div>
                        </div>
                    </div>

                    <!-- Validation errors (form incomplete or invalid) -->
                    <div *ngFor="let v of preCheck.validationErrors" class="pr__pre-row">
                        <i class="fa-solid fa-circle-exclamation pr__warn"></i>
                        <div>
                            <div class="pr__pre-title">{{ v.field || 'Form' }} · {{ v.code }}</div>
                            <div class="pr__sub">{{ v.message }}</div>
                        </div>
                    </div>

                    <!-- ok level -->
                    <div *ngIf="!preCheck.error && preCheck.level === 'ok'" class="pr__pre-row">
                        <i class="fa-solid fa-shield-check pr__ok"></i>
                        <div>
                            <div class="pr__pre-title">No anti-pattern match</div>
                            <div class="pr__sub">Learnings database scanned · no close matches in last 12 months.</div>
                        </div>
                    </div>

                    <!-- hard level -->
                    <div *ngIf="!preCheck.error && preCheck.level === 'hard'" class="pr__pre-row" style="background: #fef2f2; border-color: #fecaca;">
                        <i class="fa-solid fa-shield-halved pr__bad"></i>
                        <div>
                            <div class="pr__pre-title">Hard match — blocked (framework §5)</div>
                            <div class="pr__sub">Same channel + same tactic produced a KILL within 6 months. Either redesign to a sufficiently different tactic, or add substantive override rationale below and check the override box on submit.</div>
                        </div>
                    </div>
                    <div *ngFor="let m of preCheck.hardMatches" class="pr__pre-row">
                        <i class="fa-solid fa-skull-crossbones pr__bad"></i>
                        <div>
                            <div class="pr__pre-title">{{ m.finding || m.id || 'Anti-pattern' }}</div>
                            <div class="pr__sub">{{ m.confidence || '—' }} confidence · score {{ m.similarity != null ? m.similarity.toFixed(2) : '—' }}</div>
                        </div>
                    </div>

                    <!-- soft level -->
                    <div *ngIf="!preCheck.error && preCheck.level === 'soft'" class="pr__pre-row" style="background: #fffbeb; border-color: #fde68a;">
                        <i class="fa-solid fa-triangle-exclamation pr__warn"></i>
                        <div>
                            <div class="pr__pre-title">Soft match — acknowledgment required</div>
                            <div class="pr__sub">Similar tactic on this channel within last 12 months. Add a one-line note in <strong>Prior-learning notes</strong> explaining what's different, then submit.</div>
                        </div>
                    </div>
                    <div *ngFor="let m of preCheck.softMatches" class="pr__pre-row">
                        <i class="fa-solid fa-sparkles pr__muted"></i>
                        <div>
                            <div class="pr__pre-title">{{ m.finding || m.id || 'Prior finding' }}</div>
                            <div class="pr__sub">{{ m.confidence || '—' }} confidence · score {{ m.similarity != null ? m.similarity.toFixed(2) : '—' }}</div>
                        </div>
                    </div>

                    <!-- Suggested code + confidence (always shown when available) -->
                    <div *ngIf="!preCheck.error && (preCheck.suggestedCode || preCheck.proposalConfidence)" class="pr__pre-row">
                        <i class="fa-solid fa-tag pr__muted"></i>
                        <div>
                            <div class="pr__pre-title">Suggested code: {{ preCheck.suggestedCode || '—' }}</div>
                            <div class="pr__sub">Propose-time confidence: {{ preCheck.proposalConfidence || '—' }}</div>
                        </div>
                    </div>
                    <div class="pr__pre-empty" style="padding-top: 8px;">
                        <button class="pr__btn pr__btn--ghost pr__btn--sm" type="button" [disabled]="!canPreCheck" (click)="runPreCheck()">
                            <i class="fa-solid fa-rotate"></i> Run again
                        </button>
                    </div>
                </ng-container>
            </div>
        </mkt-ws-card>

        <mkt-ws-card *ngIf="capacity.length > 0" [hasHead]="true">
            <div head>
                <span class="pr__caps-ink">Sprint capacity</span>
            </div>
            <div class="pr__cap-body">
                <div *ngFor="let t of capacity" class="pr__cap-row">
                    <span>{{ t.label }}</span>
                    <span class="pr__mono">{{ t.free }} / {{ t.total }} free</span>
                </div>
            </div>
        </mkt-ws-card>

        <mkt-ws-card *ngIf="suggestedTier" [hasHead]="true">
            <div head><span class="pr__caps-ink">Tier suggestion</span></div>
            <div class="pr__suggest">
                <div class="pr__pre-title">Suggested: {{ suggestedTier }}</div>
                <div class="pr__sub" *ngIf="suggestedTierRationale">{{ suggestedTierRationale }}</div>
            </div>
        </mkt-ws-card>

        <div *ngIf="submitMessage" class="pr__submit-msg" [class.pr__submit-msg--err]="submitError">{{ submitMessage }}</div>
    </div>
</div>
    `,
    styles: [`
        :host { display: block; }
        .pr__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .pr__btn:hover { background: var(--mj-bg-page, #f5f5f5); }
        .pr__btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .pr__btn--primary { background: var(--mj-text-primary, #1a1a1a); color: var(--mj-bg-surface, #fff); border-color: var(--mj-text-primary, #1a1a1a); }
        .pr__btn--ghost { background: transparent; }
        .pr__btn--sm { height: 26px; padding: 4px 10px; font-size: 11.5px; }
        .pr__hint { font-size: 11px; color: var(--mj-text-secondary, #71717a); margin-top: 4px; line-height: 1.45; }
        .pr__hint--banner { margin: 4px 0 0 0; padding: 8px 12px; background: var(--mj-bg-page, #f5f5f5); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; font-size: 12px; color: var(--mj-text-primary, #1a1a1a); display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .pr__req { color: var(--mj-status-error, #be123c); font-weight: 600; }
        .pr__bad { color: var(--mj-status-error, #be123c); }
        .pr__row--2 { grid-template-columns: 1fr 1fr; }
        .pr__ut-row { display: grid; grid-template-columns: 140px 1fr 140px 32px; gap: 6px; margin-bottom: 6px; align-items: center; }

        .pr__caps, .pr__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); display: block; margin-bottom: 6px; }
        .pr__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .pr__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); }
        .pr__mono { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .pr__layout { display: grid; grid-template-columns: 2fr 1fr; gap: 24px; align-items: start; }

        .pr__form-body { padding: 20px; display: flex; flex-direction: column; gap: 16px; }
        .pr__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .pr__row--3 { grid-template-columns: 1fr 1fr 1fr; }
        .pr__input { width: 100%; box-sizing: border-box; padding: 6px 10px; height: 36px; font: inherit; font-size: 13px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); color: var(--mj-text-primary, #1a1a1a); }
        .pr__input:focus { outline: 2px solid var(--mj-brand-primary, #6366f1); outline-offset: -1px; }
        .pr__textarea { height: auto; padding: 8px 10px; resize: vertical; }

        .pr__side { display: flex; flex-direction: column; gap: 16px; }

        .pr__pre-body { display: flex; flex-direction: column; gap: 12px; }
        .pr__pre-empty { display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
        .pr__pre-row { display: flex; align-items: flex-start; gap: 8px; }
        .pr__pre-row > i { flex-shrink: 0; margin-top: 3px; }
        .pr__pre-title { font-size: 13px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); }
        .pr__ok { color: var(--mj-color-success-700, #047857); }
        .pr__warn { color: var(--mj-color-warning-700, #b45309); }
        .pr__muted { color: var(--mj-text-secondary, #71717a); }

        .pr__cap-body { display: flex; flex-direction: column; gap: 6px; font-size: 12.5px; }
        .pr__cap-row { display: flex; justify-content: space-between; }

        .pr__suggest { font-size: 12.5px; color: var(--mj-text-primary, #1a1a1a); }

        .pr__submit-msg { padding: 12px; border-radius: 6px; font-size: 12.5px; background: var(--mj-color-success-50, #ecfdf5); color: var(--mj-color-success-700, #047857); border: 1px solid var(--mj-color-success-100, #d1fae5); }
        .pr__submit-msg--err { background: var(--mj-color-error-50, #fff1f2); color: var(--mj-status-error, #be123c); border-color: var(--mj-color-error-100, #fecdd3); }

        @media (max-width: 1024px) {
            .pr__layout { grid-template-columns: 1fr; }
            .pr__row, .pr__row--3 { grid-template-columns: 1fr; }
        }
    `]
})
export class ExperimentsProposeSection {
    private _companyId = '';
    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        } else if (!v) {
            this._companyId = '';
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    tiers: marketingExperimentTierEntity[] = [];
    channels: marketingChannelConfigurationEntity[] = [];
    objectives: marketingObjectiveEntity[] = [];
    capacity: { label: string; free: number; total: number }[] = [];

    form = {
        name: '',
        hypothesis: '',
        tactic: '',
        tierId: '',
        channelId: '',
        budget: 0,
        metricName: 'conversions',
        metricBaseline: 0,
        keepThreshold: 0,
        iterateMin: 0,
        iterateMax: 0,
        killThreshold: 0,
        strategicNote: '',
        objectiveId: '',

        // Framework v2 fields
        baselineSource: 'measured' as 'measured' | 'founder-estimate' | 'cold-start',
        connectsToType: '' as '' | 'Foundation' | 'Plan' | 'Learning',
        connectsToId: '',
        proposedReason: '',
        maxDurationWeeks: 8,
        segmentCuts: '',
        priorLearningNotes: '',
    };

    /** UpdateTargets editor — at least one row required by framework §2.7. */
    updateTargets: Array<{ Type: 'Foundation' | 'Plan' | 'Learning'; TargetReference: string; Condition: 'Keep' | 'Iterate' | 'Kill' | 'Always' }> = [
        { Type: 'Learning', TargetReference: '', Condition: 'Always' },
    ];

    preChecking = false;
    preCheck: {
        ran: boolean;
        level: 'ok' | 'soft' | 'hard' | 'unknown';
        hardMatches: SimilarMatch[];
        softMatches: SimilarMatch[];
        validationErrors: Array<{ field?: string; code: string; message: string; severity?: string }>;
        suggestedCode: string;
        proposalConfidence: string;
        error: string;
    } = {
        ran: false, level: 'unknown', hardMatches: [], softMatches: [], validationErrors: [], suggestedCode: '', proposalConfidence: '', error: '',
    };

    addUpdateTarget(): void {
        this.updateTargets = [...this.updateTargets, { Type: 'Learning', TargetReference: '', Condition: 'Always' }];
    }
    removeUpdateTarget(idx: number): void {
        this.updateTargets = this.updateTargets.filter((_, i) => i !== idx);
        if (this.updateTargets.length === 0) {
            this.updateTargets = [{ Type: 'Learning', TargetReference: '', Condition: 'Always' }];
        }
    }
    /** When BaselineSource is cold-start, framework auto-promotes to Tier 3.
     *  Surface this in the UI so the user isn't surprised by the override. */
    get coldStartTierWarning(): boolean {
        return this.form.baselineSource === 'cold-start';
    }

    /**
     * Auto-fill thresholds from baseline using the framework guideline ratios.
     * Baseline 1.2 → Kill 1.0 / IterMin 1.3 / IterMax 1.99 / Keep 2.0 implies
     * 0.833 / 1.083 / 1.658 / 1.667. Rounds to 3 decimals so small baselines
     * (e.g. 0.1) don't collide on adjacent fields.
     */
    autofillThresholds(): void {
        const base = Number(this.form.metricBaseline);
        if (!base || isNaN(base)) return;
        const round3 = (n: number) => Number(n.toFixed(3));
        this.form.killThreshold = round3(base * 0.833);
        this.form.iterateMin    = round3(base * 1.083);
        this.form.iterateMax    = round3(base * 1.658);
        this.form.keepThreshold = round3(base * 1.667);
    }

    suggestedTier = '';
    suggestedTierRationale = '';

    submitting = false;
    submitMessage = '';
    submitError = false;

    channelLabel(c: marketingChannelConfigurationEntity): string {
        const parts: string[] = [];
        if (c.ContentType) parts.push(c.ContentType);
        if (c.SocialPlatform) parts.push(c.SocialPlatform);
        if (c.Maturity) parts.push(`(${c.Maturity})`);
        return parts.length > 0 ? parts.join(' · ') : c.ID.slice(0, 8);
    }

    get canPreCheck(): boolean {
        return !!(this.form.hypothesis && this.form.tactic);
    }
    get canSubmit(): boolean {
        return !!(this.form.hypothesis && this.form.tierId && this._companyId);
    }

    private async load(): Promise<void> {
        if (!this._companyId) return;
        const cid = this._companyId;
        try {
            const rv = new RunView();
            const [tierRes, chanRes, objRes] = await rv.RunViews([
                { EntityName: 'Experiment Tiers', ResultType: 'simple' },
                { EntityName: 'Channel Configurations', ExtraFilter: `CompanyID='${cid}'`, ResultType: 'simple' },
                { EntityName: 'Objectives', ExtraFilter: `CompanyID='${cid}' AND IsActive=1`, ResultType: 'simple' },
            ]);
            if (tierRes.Success) {
                this.tiers = tierRes.Results as marketingExperimentTierEntity[];
                if (!this.form.tierId && this.tiers.length > 0) this.form.tierId = this.tiers[0].ID;
            }
            if (chanRes.Success) this.channels = chanRes.Results as marketingChannelConfigurationEntity[];
            if (objRes.Success) this.objectives = objRes.Results as marketingObjectiveEntity[];

            const sprintRes = await rv.RunView({
                EntityName: 'Sprints',
                ExtraFilter: `CompanyID='${cid}' AND Status IN ('Active','Planning')`,
                OrderBy: '__mj_CreatedAt DESC',
                MaxRows: 1,
                ResultType: 'simple',
            });
            const activeSprintId = sprintRes.Success && sprintRes.Results && sprintRes.Results.length > 0
                ? (sprintRes.Results[0] as { ID: string }).ID
                : null;
            const capRes = activeSprintId
                ? await runAction('Get Sprint Slot Capacity', [
                    { Name: 'CompanyID', Value: cid },
                    { Name: 'SprintID', Value: activeSprintId },
                ])
                : { success: false, message: 'No active sprint', resultCode: 'NO_SPRINT' };
            if (capRes.success) {
                const parsed = parseActionMessageJson(capRes.message, isSprintCapacity);
                if (parsed?.tiers) {
                    this.capacity = parsed.tiers.map(t => ({
                        label: t.tier || t.name || 'Tier',
                        free: t.free ?? t.available ?? 0,
                        total: t.total ?? t.capacity ?? 0,
                    }));
                }
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsPropose] load failed: ${msg}`);
        } finally {
            this.cdr.detectChanges();
        }
    }

    async runPreCheck(): Promise<void> {
        if (!this.canPreCheck || !this._companyId) return;
        this.preChecking = true;
        this.cdr.detectChanges();

        // Suggest tier
        const sug = await runAction('Suggest Experiment Tier', [
            { Name: 'CompanyID', Value: this._companyId },
            { Name: 'Hypothesis', Value: this.form.hypothesis },
            { Name: 'Tactic', Value: this.form.tactic },
            { Name: 'Budget', Value: String(this.form.budget) },
            ...(this.form.channelId ? [{ Name: 'ChannelID', Value: this.form.channelId }] : []),
        ]);
        if (sug.success) {
            const parsed = parseActionMessageJson(sug.message, isSuggestTierResp);
            if (parsed) {
                this.suggestedTier = parsed.suggestedTier || '';
                this.suggestedTierRationale = parsed.rationale || '';
            }
        }

        // Read-only Pre-check Experiment — runs validators + 3-level similarity gate without writing.
        // Always returns SUCCESS; the JSON Message carries the full PreCheckResult.
        const res = await runAction('Pre-check Experiment', this.buildExperimentParams(/*forSubmit*/ false));
        const parsed = parseActionMessageJson(res.message, isPreCheckResp);

        const mapMatch = (m: { learningId: string; score: number; confidence: string }): SimilarMatch => ({
            id: m.learningId,
            finding: m.learningId,
            confidence: m.confidence,
            similarity: m.score,
        });

        if (!res.success || !parsed) {
            this.preCheck = {
                ran: true, level: 'unknown',
                hardMatches: [], softMatches: [], validationErrors: [],
                suggestedCode: '', proposalConfidence: '',
                error: res.message || 'Pre-check failed unexpectedly.',
            };
        } else {
            this.preCheck = {
                ran: true,
                level: parsed.level ?? 'ok',
                hardMatches: (parsed.hardMatches ?? []).map(mapMatch),
                softMatches: (parsed.softMatches ?? []).map(mapMatch),
                validationErrors: (parsed.validationErrors ?? []).filter(e => (e.severity ?? 'error') === 'error'),
                suggestedCode: parsed.suggestedCode ?? '',
                proposalConfidence: parsed.proposalConfidence?.label ?? '',
                error: '',
            };
        }
        this.preChecking = false;
        this.cdr.detectChanges();
    }

    /**
     * Single source of truth for the param list both Pre-check and Propose send.
     * `forSubmit` toggles the override + prior-learning-notes inclusion that only
     * the write path needs.
     */
    private buildExperimentParams(forSubmit: boolean): { Name: string; Value: string }[] {
        const params: { Name: string; Value: string }[] = [
            { Name: 'CompanyID', Value: this._companyId },
            { Name: 'Hypothesis', Value: this.form.hypothesis || '' },
            { Name: 'Tactic', Value: this.form.tactic || '' },
            { Name: 'MetricName', Value: this.form.metricName || 'conversions' },
            { Name: 'MetricBaseline', Value: String(this.form.metricBaseline ?? 0) },
            { Name: 'KeepThreshold', Value: String(this.form.keepThreshold ?? 0) },
            { Name: 'IterateRangeMin', Value: String(this.form.iterateMin ?? 0) },
            { Name: 'IterateRangeMax', Value: String(this.form.iterateMax ?? 0) },
            { Name: 'KillThreshold', Value: String(this.form.killThreshold ?? 0) },
            { Name: 'Budget', Value: String(this.form.budget ?? 0) },
            { Name: 'BudgetPerSprint', Value: String((this.form.budget ?? 0) / 2) },
            { Name: 'StrategicConnectionNote', Value: this.form.strategicNote || '' },
            { Name: 'DecisionDate', Value: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10) },
            { Name: 'BaselineSource', Value: this.form.baselineSource || 'measured' },
            { Name: 'MaxDurationWeeks', Value: String(this.form.maxDurationWeeks || 8) },
            { Name: 'ProposedBy', Value: 'user' },
            { Name: 'UpdateTargets', Value: JSON.stringify(this.updateTargets.filter(t => t.TargetReference?.trim())) },
        ];
        if (this.form.name) params.push({ Name: 'Name', Value: this.form.name });
        if (this.form.tierId) params.push({ Name: 'ExperimentTierID', Value: this.form.tierId });
        if (this.form.channelId) params.push({ Name: 'ChannelID', Value: this.form.channelId });
        if (this.form.objectiveId) params.push({ Name: 'ObjectiveID', Value: this.form.objectiveId });
        if (this.form.connectsToType) params.push({ Name: 'ConnectsToType', Value: this.form.connectsToType });
        if (this.form.connectsToId) params.push({ Name: 'ConnectsToID', Value: this.form.connectsToId });
        if (this.form.proposedReason) params.push({ Name: 'ProposedReason', Value: this.form.proposedReason });
        if (this.form.segmentCuts) params.push({ Name: 'SegmentCuts', Value: this.form.segmentCuts });
        if (forSubmit) {
            // For submit path, decide whether to override based on the pre-check level.
            const override = this.preCheck.level === 'hard' && !!this.form.priorLearningNotes?.trim();
            params.push({ Name: 'OverridePriorLearning', Value: String(override) });
            if (this.form.priorLearningNotes) params.push({ Name: 'PriorLearningNotes', Value: this.form.priorLearningNotes });
        }
        return params;
    }

    async submit(): Promise<void> {
        if (!this.canSubmit || !this._companyId) return;
        this.submitting = true;
        this.submitMessage = '';
        this.submitError = false;
        this.cdr.detectChanges();

        const params = this.buildExperimentParams(/*forSubmit*/ true);
        const res = await runAction('Propose Experiment', params);
        if (res.success) {
            this.submitMessage = `Experiment proposed. Result: ${res.message}`;
            // Try to find the newly created row by hypothesis match and route to detail
            try {
                const rv = new RunView();
                const r = await rv.RunView<marketingExperimentEntity>({
                    EntityName: 'Experiments',
                    ExtraFilter: `CompanyID='${this._companyId}' AND Hypothesis=N'${this.form.hypothesis.replace(/'/g, "''")}'`,
                    OrderBy: '__mj_CreatedAt DESC',
                    MaxRows: 1,
                    ResultType: 'simple',
                });
                if (r.Success && r.Results && r.Results.length > 0) {
                    const id = r.Results[0].ID;
                    setTimeout(() => this.navigateWithId('experiments/detail', id), 600);
                }
            } catch (err: unknown) {
                const msg = err instanceof Error ? err.message : String(err);
                LogError(`[ExperimentsPropose] post-submit lookup failed: ${msg}`);
            }
        } else {
            this.submitError = true;
            this.submitMessage = `Failed (${res.resultCode}): ${res.message}`;
        }
        this.submitting = false;
        this.cdr.detectChanges();
    }
}
